import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ImportButtons } from "@/components/admin/import/ImportButtons";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  WA_TEMPLATE_KEYS,
  type WaTemplateKey,
  type WaTemplateRow,
  deleteWhatsAppTemplate,
  listWhatsAppTemplates,
  testWhatsAppConfiguration,
  upsertWhatsAppTemplate,
} from "@/lib/admin/whatsapp-templates.functions";

export const Route = createFileRoute("/_admin/admin/whatsapp-templates")({
  component: Page,
});

type Editing = {
  id?: string;
  template_key: WaTemplateKey;
  meta_template_name: string;
  language_code: string;
  enabled: boolean;
  variable_map_json: string;
  notes: string;
};

function empty(key: WaTemplateKey): Editing {
  return {
    template_key: key,
    meta_template_name: "",
    language_code: "en",
    enabled: false,
    variable_map_json: "{}",
    notes: "",
  };
}

function Page() {
  const listFn = useServerFn(listWhatsAppTemplates);
  const saveFn = useServerFn(upsertWhatsAppTemplate);
  const delFn = useServerFn(deleteWhatsAppTemplate);
  const testFn = useServerFn(testWhatsAppConfiguration);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "whatsapp-templates"],
    queryFn: () => listFn(),
  });

  const [drafts, setDrafts] = useState<Record<WaTemplateKey, Editing>>(
    () => Object.fromEntries(WA_TEMPLATE_KEYS.map((k) => [k, empty(k)])) as Record<WaTemplateKey, Editing>,
  );
  const [confirmDelete, setConfirmDelete] = useState<Editing | null>(null);

  useEffect(() => {
    if (!data) return;
    const next = Object.fromEntries(WA_TEMPLATE_KEYS.map((k) => [k, empty(k)])) as Record<WaTemplateKey, Editing>;
    for (const row of data as WaTemplateRow[]) {
      next[row.template_key] = {
        id: row.id,
        template_key: row.template_key,
        meta_template_name: row.meta_template_name,
        language_code: row.language_code,
        enabled: row.enabled,
        variable_map_json: JSON.stringify(row.variable_map ?? {}, null, 2),
        notes: row.notes ?? "",
      };
    }
    setDrafts(next);
  }, [data]);

  const save = useMutation({
    mutationFn: async (d: Editing) => {
      if (!d.meta_template_name.trim()) {
        throw new Error("Meta template name is required.");
      }
      let variable_map: unknown = {};
      try {
        variable_map = JSON.parse(d.variable_map_json || "{}");
      } catch {
        throw new Error("variable_map must be valid JSON");
      }
      return saveFn({
        data: {
          id: d.id ?? null,
          template_key: d.template_key,
          meta_template_name: d.meta_template_name.trim(),
          language_code: d.language_code.trim() || "en",
          enabled: d.enabled,
          variable_map: variable_map as never,
          notes: d.notes || null,
        },
      });
    },
    onSuccess: async () => {
      toast.success("Template saved");
      await refetch();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: async () => {
      toast.success("Template deleted");
      setConfirmDelete(null);
      await refetch();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  const test = useMutation({
    mutationFn: () => testFn(),
    onSuccess: (res) => {
      if ((res as { ok: boolean }).ok) {
        toast.success("WhatsApp credentials OK");
      } else {
        toast.error(`Config test failed: ${(res as { reason?: string; message?: string }).reason ?? "error"} ${(res as { message?: string }).message ?? ""}`);
      }
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Test failed"),
  });

  return (
    <div>
      <AdminPageHeader
        title="WhatsApp Templates"
        description="Map each template key to an approved Meta template. Sends do not begin until the matching template is enabled here AND approved on Meta."
        actions={<ImportButtons moduleId="whatsapp-templates" />}
      />

      <div className="mb-4 flex justify-end">
        <Button variant="outline" onClick={() => test.mutate()} disabled={test.isPending}>
          {test.isPending ? "Testing…" : "Test WhatsApp configuration"}
        </Button>
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}

      <div className="space-y-4">
        {WA_TEMPLATE_KEYS.map((key) => {
          const d = drafts[key];
          return (
            <Card key={key}>
              <CardHeader>
                <CardTitle className="text-base">{key}</CardTitle>
                <CardDescription>
                  {d.id ? "Existing template" : "Not configured yet"}
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Meta template name</Label>
                  <Input
                    value={d.meta_template_name}
                    onChange={(e) =>
                      setDrafts((prev) => ({ ...prev, [key]: { ...d, meta_template_name: e.target.value } }))
                    }
                    placeholder="order_received_v1"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Language code</Label>
                  <Input
                    value={d.language_code}
                    onChange={(e) =>
                      setDrafts((prev) => ({ ...prev, [key]: { ...d, language_code: e.target.value } }))
                    }
                    placeholder="en"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Variable map (JSON)</Label>
                  <Textarea
                    rows={4}
                    value={d.variable_map_json}
                    onChange={(e) =>
                      setDrafts((prev) => ({ ...prev, [key]: { ...d, variable_map_json: e.target.value } }))
                    }
                    className="mt-1 font-mono text-xs"
                    placeholder={`{"1": "order_ref", "2": "customer_name"}`}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Maps Meta template placeholders {"{{1}} {{2}}"} to order fields
                    (order_ref, customer_name, meal_plan_title, meal_type, quantity,
                    preferred_delivery_date, preferred_delivery_time, neighbourhood,
                    pincode, status).
                  </p>
                </div>
                <div className="md:col-span-2">
                  <Label>Notes</Label>
                  <Textarea
                    rows={2}
                    value={d.notes}
                    onChange={(e) =>
                      setDrafts((prev) => ({ ...prev, [key]: { ...d, notes: e.target.value } }))
                    }
                    className="mt-1"
                  />
                </div>
                <div className="flex items-center justify-between rounded-md border p-3 md:col-span-2">
                  <div>
                    <Label className="text-sm">Enabled</Label>
                    <p className="text-xs text-muted-foreground">Must be enabled AND approved on Meta.</p>
                  </div>
                  <Switch
                    checked={d.enabled}
                    onCheckedChange={(v) =>
                      setDrafts((prev) => ({ ...prev, [key]: { ...d, enabled: v } }))
                    }
                  />
                </div>
                <div className="md:col-span-2 flex flex-wrap items-center justify-end gap-2">
                  {d.id ? (
                    <Button variant="ghost" onClick={() => setConfirmDelete(d)} disabled={remove.isPending}>
                      Delete
                    </Button>
                  ) : null}
                  <Button
                    onClick={() => {
                      if (!d.meta_template_name.trim()) {
                        toast.error("Meta template name is required.");
                        return;
                      }
                      save.mutate(d);
                    }}
                    disabled={save.isPending}
                  >
                    {save.isPending ? "Saving…" : "Save"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <AlertDialog open={confirmDelete !== null} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this template?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the "{confirmDelete?.template_key}" template configuration permanently. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete?.id && remove.mutate(confirmDelete.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}